import { useState, useMemo } from 'react';
import { 
  BarChart3, 
  Calendar,
  Filter,
  Download,
  Users,
  TrendingUp,
  Clock,
  Target,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Project, Task, TimeLog, User } from '../types';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';
import { SummaryStatCard } from '../components/StatCards';
import SurfaceCard from '../components/SurfaceCard';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { WIDGET_MUTED_TEXT_CLASS } from '../constants/widgetTheme';
import ReportSectionCard from '../components/ReportSectionCard.tsx';

export default function StakeholderReports() {
  const { state } = useApp();
  const { projects, tasks, users } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    projects: Project[];
    tasks: Task[];
    users: User[];
  };
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDeveloper, setSelectedDeveloper] = useState<string>('all');
  const [filterBy, setFilterBy] = useState<'all' | 'project' | 'epic'>('all');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  const toTime = (value: unknown, fallback: number = 0): number => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.getTime() : fallback;
  };

  const formatDate = (
    value: unknown,
    options?: Intl.DateTimeFormatOptions,
    fallback: string = 'Unknown date'
  ): string => {
    const parsedDate = parseDateLike(value);
    return parsedDate ? parsedDate.toLocaleDateString('en-US', options) : fallback;
  };

  const developers = users.filter(u => u.role === 'developer');

  // Get all time logs for selected criteria
  const filteredTimeLogs = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    const logs: Array<TimeLog & { 
      task: Task; 
      user: User; 
      projectName: string;
      epicTitle?: string;
    }> = [];
    
    tasks.forEach(task => {
      const taskProject = projects.find(p => p.id === task.projectId);
      const epicTitle = task.epicId ? tasks.find(t => t.id === task.epicId)?.title : undefined;
      
      // Apply filters
      if (filterBy === 'project' && selectedFilter !== 'all' && task.projectId !== selectedFilter) {
        return;
      }
      if (filterBy === 'epic' && selectedFilter !== 'all' && task.epicId !== selectedFilter) {
        return;
      }
      
      task.timeLogs
        .filter(log => {
          if (selectedDeveloper !== 'all' && log.userId !== selectedDeveloper) return false;
          const logDate = parseDateLike(log.date);
          if (!logDate) return false;
          return logDate >= startDate && logDate <= endDate;
        })
        .forEach(log => {
          const user = users.find(u => u.id === log.userId);
          if (user) {
            logs.push({
              ...log,
              task,
              user,
              projectName: taskProject?.name || 'Unknown Project',
              epicTitle
            });
          }
        });
    });

    return logs.sort((a, b) => toTime(b.date) - toTime(a.date));
  }, [tasks, users, projects, selectedMonth, selectedDeveloper, filterBy, selectedFilter]);

  // Calculate statistics by developer
  const developerStats = useMemo(() => {
    const stats: Record<string, {
      user: User;
      totalHours: number;
      tasksWorked: number;
      projects: Set<string>;
      avgRating: number;
      ratedTasks: number;
    }> = {};

    developers.forEach(dev => {
      stats[dev.id] = {
        user: dev,
        totalHours: 0,
        tasksWorked: 0,
        projects: new Set(),
        avgRating: 0,
        ratedTasks: 0
      };
    });

    const tasksByDev: Record<string, Set<string>> = {};
    
    filteredTimeLogs.forEach(log => {
      if (stats[log.userId]) {
        stats[log.userId].totalHours += log.hours;
        stats[log.userId].projects.add(log.task.projectId);
        
        if (!tasksByDev[log.userId]) tasksByDev[log.userId] = new Set();
        tasksByDev[log.userId].add(log.task.id);
      }
    });

    // Count tasks and calculate ratings
    Object.keys(tasksByDev).forEach(userId => {
      stats[userId].tasksWorked = tasksByDev[userId].size;
      
      const userTasks = Array.from(tasksByDev[userId])
        .map(taskId => tasks.find(t => t.id === taskId))
        .filter(t => t && t.rating);
      
      if (userTasks.length > 0) {
        stats[userId].avgRating = userTasks.reduce((sum, t) => sum + (t!.rating || 0), 0) / userTasks.length;
        stats[userId].ratedTasks = userTasks.length;
      }
    });

    return Object.values(stats).sort((a, b) => b.totalHours - a.totalHours);
  }, [filteredTimeLogs, developers, tasks]);

  // Overall statistics
  const overallStats = useMemo(() => {
    const totalHours = filteredTimeLogs.reduce((sum, log) => sum + log.hours, 0);
    const uniqueDevelopers = new Set(filteredTimeLogs.map(log => log.userId)).size;
    const uniqueTasks = new Set(filteredTimeLogs.map(log => log.task.id)).size;
    const uniqueProjects = new Set(filteredTimeLogs.map(log => log.task.projectId)).size;
    
    return { totalHours, uniqueDevelopers, uniqueTasks, uniqueProjects };
  }, [filteredTimeLogs]);

  // Get filter options
  const filterOptions = useMemo(() => {
    if (filterBy === 'project') {
      return projects.map(p => ({ value: p.id, label: p.name }));
    } else if (filterBy === 'epic') {
      const epics = tasks.filter(t => t.type === 'Feature' && !t.epicId);
      return epics.map(e => ({ value: e.id, label: e.title }));
    }
    return [];
  }, [filterBy, projects, tasks]);

  const handleExport = () => {
    const headers = ['Developer', 'Date', 'Project', 'Task', 'Description', 'Hours'];
    const rows = filteredTimeLogs.map(log => [
      log.user.name,
      formatDate(log.date),
      log.projectName,
      log.task.title,
      log.description,
      log.hours.toString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-hours-${selectedMonth}.csv`;
    a.click();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Team Reports</h1>
              <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Developer productivity and time tracking</p>
            </div>
          </div>
          <Button onClick={handleExport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Filters */}
        <SurfaceCard className="mt-4 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Calendar className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
              <input
                type="date"
                title="Select report month"
                aria-label="Select report month"
                value={`${selectedMonth}-01`}
                onChange={(e) => setSelectedMonth(e.target.value.slice(0, 7))}
                className={APP_NATIVE_FIELD_CLASS}
              />
            </div>

            <div className="flex items-center gap-2">
              <Users className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
              <select
                title="Filter by developer"
                aria-label="Filter by developer"
                value={selectedDeveloper}
                onChange={(e) => setSelectedDeveloper(e.target.value)}
                className={APP_NATIVE_FIELD_CLASS}
              >
                <option value="all">All Developers</option>
                {developers.map(dev => (
                  <option key={dev.id} value={dev.id}>{dev.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Filter className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
              <select
                title="Filter scope"
                aria-label="Filter scope"
                value={filterBy}
                onChange={(e) => {
                  setFilterBy(e.target.value as any);
                  setSelectedFilter('all');
                }}
                className={APP_NATIVE_FIELD_CLASS}
              >
                <option value="all">No Filter</option>
                <option value="project">By Project</option>
                <option value="epic">By Epic</option>
              </select>
            </div>

            {filterBy !== 'all' && (
              <select
                title={`Filter by ${filterBy}`}
                aria-label={`Filter by ${filterBy}`}
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className={APP_NATIVE_FIELD_CLASS}
              >
                <option value="all">All {filterBy === 'project' ? 'Projects' : 'Epics'}</option>
                {filterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>
        </SurfaceCard>
      </div>

      <div>
        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <SummaryStatCard
            icon={<Clock className="w-5 h-5" />}
            label="Total Hours"
            value={`${overallStats.totalHours.toFixed(1)}h`}
            color="blue"
          />
          <SummaryStatCard
            icon={<Users className="w-5 h-5" />}
            label="Active Developers"
            value={overallStats.uniqueDevelopers.toString()}
            color="green"
          />
          <SummaryStatCard
            icon={<Target className="w-5 h-5" />}
            label="Tasks Worked"
            value={overallStats.uniqueTasks.toString()}
            color="purple"
          />
          <SummaryStatCard
            icon={<TrendingUp className="w-5 h-5" />}
            label="Projects Active"
            value={overallStats.uniqueProjects.toString()}
            color="orange"
          />
        </div>

        {/* Developer Performance */}
        <ReportSectionCard
          className="mb-8"
          title="Developer Performance"
          subtitle="Hours logged and task ratings"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-border bg-muted/50">
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Developer
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Total Hours
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Tasks Worked
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Projects
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Avg Rating
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${WIDGET_MUTED_TEXT_CLASS}`}>
                    Rated Tasks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {developerStats.map((stat) => (
                  <tr key={stat.user.id} className="hover:bg-accent/20">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                          {stat.user.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-foreground">{stat.user.name}</div>
                          <div className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{stat.user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">
                        {stat.totalHours.toFixed(1)}h
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {stat.tasksWorked}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {stat.projects.size}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stat.avgRating > 0 ? (
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-chart-5" />
                          <span className="font-semibold">{stat.avgRating.toFixed(1)}</span>
                          <span className={WIDGET_MUTED_TEXT_CLASS}>/5</span>
                        </div>
                      ) : (
                        <span className={WIDGET_MUTED_TEXT_CLASS}>-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {stat.ratedTasks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {developerStats.length === 0 && (
            <div className="p-12 text-center">
              <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground/70" />
              <h3 className="mb-2 text-lg font-semibold text-foreground">No data available</h3>
              <p className={WIDGET_MUTED_TEXT_CLASS}>No time logs found for the selected criteria</p>
            </div>
          )}
        </ReportSectionCard>

        {/* Detailed Time Logs */}
        <ReportSectionCard title="Detailed Time Logs" subtitle={`${filteredTimeLogs.length} entries`}>
          <div className="max-h-[600px] divide-y divide-border overflow-y-auto">
            {filteredTimeLogs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-accent/20">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {log.user.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{log.user.name}</div>
                        <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                          {formatDate(log.date, {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="rounded bg-primary/10 px-2 py-1 text-xs text-primary">
                        {log.projectName}
                      </span>
                      {log.epicTitle && (
                        <span className="rounded bg-chart-4/10 px-2 py-1 text-xs text-chart-4">
                          Epic: {log.epicTitle}
                        </span>
                      )}
                    </div>
                    <div className="mb-1 text-sm font-medium text-foreground">{log.task.title}</div>
                    <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{log.description}</p>
                  </div>
                  <span className="ml-4 rounded-full bg-chart-2/10 px-3 py-1 font-semibold text-chart-2">
                    {log.hours}h
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredTimeLogs.length === 0 && (
            <div className="p-12 text-center">
              <Clock className="mx-auto mb-4 h-12 w-12 text-muted-foreground/70" />
              <h3 className="mb-2 text-lg font-semibold text-foreground">No time logs found</h3>
              <p className={WIDGET_MUTED_TEXT_CLASS}>Try adjusting your filters</p>
            </div>
          )}
        </ReportSectionCard>
      </div>
    </div>
  );
}